"use client";
import { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";

import { AuctionCard } from "./AuctionCard";
import { COLORS } from "@/data/main-color";
import { useLocale } from "next-intl";
import HeroImg from "./HeroImg";
import HeroText from "./HeroText";

export default function LuxuryHeroTSX() {
  const locale = useLocale();

  const isAr = locale === "ar";
  const rootRef = useRef<HTMLElement>(null);

  const mouseX = useMotionValue(0.5);
  const mouseY = useMotionValue(0.5);
  const glowX = useSpring(
    useTransform(mouseX, (v) => `${v * 100}%`),
    { stiffness: 50, damping: 20 },
  );
  const glowY = useSpring(
    useTransform(mouseY, (v) => `${v * 100}%`),
    { stiffness: 50, damping: 20 },
  );

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const handleMouseMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      mouseX.set((e.clientX - rect.left) / rect.width);
      mouseY.set((e.clientY - rect.top) / rect.height);
    };
    el.addEventListener("mousemove", handleMouseMove);
    return () => el.removeEventListener("mousemove", handleMouseMove);
  }, [mouseX, mouseY]);

  return (
    <>
      <style jsx global>{`
        @import url("https://fonts.googleapis.com/css2?family=Cairo:wght@300;400;600;700;900&family=Cormorant:ital,wght@0,300;0,400;0,600;0,700;1,300;1,400&display=swap");
        .font-cairo {
          font-family: "Cairo", sans-serif;
        }
        .font-cormorant {
          font-family: "Cormorant", serif;
        }

        /* تأثير قطع الزرار البروميميوم */
        .clip-premium {
          clip-path: polygon(10px 0%, 100% 0%, calc(100% - 10px) 100%, 0% 100%);
        }
      `}</style>

      <section
        ref={rootRef}
        dir={isAr ? "rtl" : "ltr"}
        className={`relative min-h-screen flex flex-col justify-center ${COLORS.bg} ${COLORS.cream} overflow-hidden ${isAr ? "font-cairo" : "font-cormorant"}`}
      >
        <motion.div
          className="absolute pointer-events-none z-0 w-225 h-225 rounded-full opacity-60"
          style={{
            background: `radial-gradient(circle, rgba(200,169,110,0.08) 0%, transparent 65%)`,
            translateX: "-50%",
            translateY: "-50%",
            left: glowX,
            top: glowY,
          }}
        />

        <div className="absolute inset-0 z-0 pointer-events-none bg-[linear-gradient(rgba(200,169,110,0.035)_1px,transparent_1px),linear-gradient(90deg,rgba(200,169,110,0.035)_1px,transparent_1px)] bg-size-[64px_64px]" />

        <div
          className={`absolute z-0 pointer-events-none top-[-15%] ${isAr ? "left-[-10%]" : "right-[-10%]"} w-175 h-175 rounded-full border border-[#C8A96E]/7 animate-[slowSpin_80s_linear_infinite]`}
        >
          <div className="absolute inset-10 rounded-full border border-dashed border-[#C8A96E]/5" />
          <div className="absolute inset-25 rounded-full border border-[#C8A96E]/4" />
        </div>

        <div
          className={`absolute z-0 pointer-events-none top-0 bottom-0 ${isAr ? "right-[52%]" : "left-[52%]"} w-px bg-linear-to-b from-transparent via-[#C8A96E]/25 to-transparent opacity-40`}
        />

        {[
          "top-7 left-7",
          "top-7 right-7",
          "bottom-7 left-7",
          "bottom-7 right-7",
        ].map((pos, i) => (
          <div
            key={i}
            className={`absolute z-10 pointer-events-none w-9 h-9 ${pos}`}
          >
            <div
              className={`absolute w-full h-full ${pos.includes("top") ? "border-t" : "border-b"} ${pos.includes("left") ? "border-l" : "border-r"} border-[#C8A96E]/25`}
            />
          </div>
        ))}

        <HeroImg isAr={isAr} />

        <div className="absolute inset-0 z-5 pointer-events-none">
          <div className="relative w-full h-full container mx-auto px-14">
            <AuctionCard isAr={isAr} />
          </div>
        </div>

        <HeroText isAr={isAr} />

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="absolute bottom-9 left-1/2 -translate-x-1/2 z-6"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
            className="flex flex-col items-center gap-1.5 color-[#EDE5D6]/45 text-[11px] tracking-[2px] uppercase cursor-pointer transition-colors duration-300 hover:text-[#C8A96E] font-cairo"
          >
            <span>{isAr ? "اكتشف" : "Explore"}</span>
            <ChevronDown size={16} color="#C8A96E" />
          </motion.div>
        </motion.div>
      </section>

      <style jsx global>{`
        @keyframes slowSpin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </>
  );
}
